import React, { useEffect, useState } from "react";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useInboxSocket } from "../lib/useInboxSocket";
import MessageThread from "../components/MessageThread";

/** Designer inbox — assigned conversations only, no customer contact details. */
export default function DesignerInbox() {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const { liveMessage, connected, liveConversation, liveTyping, sendTyping } = useInboxSocket(true);

  const load = () => api.get("/designer/inbox").then(r => {
    setConvs(r.data || []);
    if (!activeId && r.data?.[0]) setActiveId(r.data[0].id);
  });

  useEffect(() => { load(); }, []);

  // Refresh list when a live update arrives (debounced)
  useEffect(() => {
    if (!liveConversation) return;
    const t = setTimeout(load, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveConversation]);

  const active = convs.find(c => c.id === activeId);

  return (
    <div className="space-y-3 h-full">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Conversations</h2>
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-400"}`} />
          {connected ? "Live" : "Reconnecting..."}
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3" data-testid="designer-privacy-banner">
        <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900">
          Customer contact details are hidden by design. All communication runs through HouseDesignWala.
        </div>
      </div>
      <div className="grid lg:grid-cols-[300px_1fr] gap-3 h-[70vh]">
        <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 overflow-y-auto" data-testid="designer-conv-list">
          {convs.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No assigned conversations yet.</div>
          ) : convs.map(c => (
            <button
              key={c.id}
              onClick={() => setActiveId(c.id)}
              data-testid={`conv-item-${c.id}`}
              className={`w-full text-left p-3 border-b border-gray-100 hover:bg-[#FEF3E2]/30 ${activeId === c.id ? "bg-[#FEF3E2]/50" : ""}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1a3c5e] text-white font-bold flex items-center justify-center flex-shrink-0">
                  {(c.title || "C")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <div className="font-semibold text-[#1a3c5e] text-sm truncate">{c.title}</div>
                    {c.unread > 0 && <span className="bg-[#F7941D] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{c.unread}</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{c.last_message_preview || c.subtitle || "—"}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {active ? (
          <MessageThread
            conversationId={active.id}
            role="designer"
            apiBase={`/designer/inbox/${active.id}`}
            canInternalNote={true}
            liveMessage={liveMessage}
            liveTyping={liveTyping}
            sendTyping={sendTyping}
            onUnreadChange={load}
            header={
              <div className="px-4 py-3 border-b border-gray-100 bg-white">
                <div className="font-display font-bold text-[#1a3c5e]" data-testid="designer-thread-title">{active.title}</div>
                <div className="text-[11px] text-gray-500">{active.subtitle} · Order #{(active.order_id || "").slice(0,8) || "—"}</div>
              </div>
            }
          />
        ) : (
          <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 flex items-center justify-center text-gray-400 text-sm">
            <MessageCircle className="w-10 h-10 mr-3 text-gray-300" />
            Select a conversation
          </div>
        )}
      </div>
    </div>
  );
}
